import { useToken } from "@gluestack-style/react";
import { ButtonSelect, SelectOption } from "components/form/ButtonSelect";
import Container from "components/general/Container";
import ItemFilterList from "components/general/ItemFilterList";
import ItemFilterListMobile from "components/general/ItemFilterListMobile";
import ScrollContainer from "components/general/ScrollContainer";
import ItemCardList from "components/item/ItemCardList";
import { Box, Button, HStack, Icon, Pressable, Text, Typography, View } from "components/ui";
import { ChevronDown, ChevronRight, Grip, List, SearchX } from "lucide-react-native";
import React, { useEffect, useState } from "react";
import { ActivityIndicator } from "react-native";
import { ItemRead } from "store/abcoApi";
import { useFilterSettings } from "utils/hooks/useFilterSettings";
import { useIsMobile } from "utils/hooks/useIsMobile";

interface ItemListingProps {
	children?: React.ReactNode;
	topCategory: any;
	category: any;
	itemCollection: ItemRead[];
	isOnSearch?: boolean;
	handleFilterChange?: (value: any) => void;
	totalFoundItems?: number;
	isFetchingItems?: boolean;
	withFooter?: boolean;
}

export default function ItemListing({ children, topCategory, category, itemCollection, isOnSearch, handleFilterChange, totalFoundItems, isFetchingItems, withFooter = false }: ItemListingProps) {
	const isMobile = useIsMobile();
	const [isGridView, setIsGridView] = React.useState(true);
	const { isFilterSettingsOpen, setIsFilterOpen } = useFilterSettings();
	const [isInitialMount, setIsInitialMount] = React.useState(true);
	const sortOptions: SelectOption[] = [
		{ id: "recommended", label: "Recommended", value: "recommended" },
		{ id: "az", label: "Name (A-Z)", value: "az" },
		{ id: "za", label: "Name (Z-A)", value: "za" },
		{ id: "price-asc", label: "Price: Low to High", value: "price-asc" },
		{ id: "price-desc", label: "Price: High to Low", value: "price-desc" },
	];

	React.useEffect(() => {
		setIsInitialMount(false);
	}, []);

	const defaultSortOption = sortOptions[0];
	const [sortOption, setSortOption] = React.useState<SelectOption>(defaultSortOption);
	const resolvedPrimary = useToken("colors", "primarylight");

	useEffect(() => {
		handleFilterChange &&
			handleFilterChange({
				sortBy: sortOption.value,
			});
	}, [sortOption]);

	const showLoading = isInitialMount || isFetchingItems || !itemCollection;

	const [mobileFilterValues, setMobileFilterValues] = useState({});

	if (isMobile) {
		return (
			<Box flex={1} height={"$full"}>
				{showLoading ? (
					<Box flex={1} justifyContent="center" alignItems="center" minHeight={300}>
						<ActivityIndicator size="large" style={{ transform: [{ scaleX: 2 }, { scaleY: 2 }] }} />
					</Box>
				) : (
					<ItemCardList
						list={itemCollection}
						scrollEnabled={true}
						isGridView={isGridView}
						isOnSearch={isOnSearch}
						header={
							<View bg="$basicwhite">
								{children}
								<Box flex={1}>
									<ItemFilterListMobile
										mobileFilterValues={mobileFilterValues}
										setMobileFilterValues={setMobileFilterValues}
										onChange={handleFilterChange}
										setIsGridView={setIsGridView}
										isGridView={isGridView}
										category={topCategory}
										subCategory={category}
										itemCount={totalFoundItems || 0}
									/>
								</Box>
							</View>
						}
					/>
				)}
			</Box>
		);
	}

	return (
		<ScrollContainer withFooter={withFooter} withPadding={isOnSearch ? false : true}>
			{children && <Container>{children}</Container>}
			<Box bg="$neutral25">
				<Container>
					<View
						w={"$full"}
						sx={{
							paddingVertical: 40,
							gap: 16,
							flexDirection: "row",
							paddingHorizontal: 48,
						}}
					>
						{isFilterSettingsOpen && (
							<Box flex={1}>
								<ItemFilterList category={topCategory} subCategory={category} onChange={handleFilterChange} />
							</Box>
						)}

						<Box flex={3}>
							<Box position="relative" zIndex={9} paddingHorizontal={16}>
								<HStack gap={20} alignItems="center">
									{!isFilterSettingsOpen && (
										<Button
											onPress={() => {
												setIsFilterOpen(!isFilterSettingsOpen);
											}}
											alignSelf="center"
											alignItems="center"
											gap={8}
										>
											<Text color="$primarylight">Hide/show filter</Text>
											<Icon as={isFilterSettingsOpen ? ChevronDown : ChevronRight} color="$primarylight" />
										</Button>
									)}

									<Typography.Paragraph weight="normal">
										<Text fontWeight="bold">{totalFoundItems}</Text> products found
									</Typography.Paragraph>
									<Box flexDirection="row" alignItems="center" position="relative" zIndex={1}>
										<Typography.Paragraph weight="normal">Sort by: </Typography.Paragraph>
										<ButtonSelect
											options={sortOptions}
											value={[sortOption]}
											onChange={(selected: SelectOption[]) => {
												setSortOption(selected[0]);
											}}
											placeholder="Select sorting..."
											label="Sort"
											customTrigger={({ selectedItems, open, ref }) => (
												<Pressable style={{ borderWidth: 0 }} ref={ref} onPress={open}>
													<HStack gap={6} alignItems="center">
														<Typography color="$primarylight" weight="bold">
															{selectedItems.length > 0 ? selectedItems[0].label : "Select an option"}
														</Typography>
														<ChevronDown color="black" />
													</HStack>
												</Pressable>
											)}
										/>
									</Box>
									<HStack marginLeft={"auto"} gap={12}>
										<Button
											onPress={() => setIsGridView(true)}
											gap={6}
											bg={isGridView ? "$primarylight" : "$basicwhite"}
											borderRadius={12}
											paddingHorizontal={20}
											paddingVertical={12}
											borderWidth={1}
											borderColor="$primarylight"
										>
											<Icon as={Grip} color={isGridView ? "$basicwhite" : "$primarylight"} />
											<Typography
												sx={{
													color: isGridView ? "$basicwhite" : "$primarylight",
												}}
											>
												Grid
											</Typography>
										</Button>
										<Button
											onPress={() => setIsGridView(false)}
											gap={6}
											borderRadius={12}
											bg={!isGridView ? "$primarylight" : "$basicwhite"}
											paddingHorizontal={20}
											paddingVertical={12}
											borderWidth={1}
											borderColor="$primarylight"
										>
											<Icon as={List} color={!isGridView ? "$basicwhite" : "$primarylight"} />
											<Typography
												sx={{
													color: !isGridView ? "$basicwhite" : "$primarylight",
												}}
											>
												List
											</Typography>
										</Button>
									</HStack>
								</HStack>
							</Box>
							{showLoading ? (
								<Box flex={1} justifyContent="center" alignItems="center" minHeight={300}>
									<ActivityIndicator size="large" style={{ transform: [{ scaleX: 2 }, { scaleY: 2 }] }} />
								</Box>
							) : itemCollection.length === 0 ? (
								<Box flex={1} justifyContent="center" alignItems="center" minHeight={300}>
									<SearchX size={100} color={resolvedPrimary} />
									<Typography.H5>Oops! No item found.</Typography.H5>
								</Box>
							) : (
								<>
									<ItemCardList isOnSearch={isOnSearch} isGridView={isGridView} list={itemCollection} scrollEnabled={false} />
								</>
							)}
						</Box>
					</View>
				</Container>
			</Box>
		</ScrollContainer>
	);
}
